import { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import { FeeTypeSlug, PriceAmount, PriceAmountWithDetails, PriceFeeDetail } from '@/api';
import { I18nWording, useDatabaseTranslation } from '@/services/i18n';
import {
  displayQuantityNotCharged,
  displayQuantityToBeCharged,
} from '@/utils/articleActionSpareParts';

export type Row = {
  label: MessageDescriptor | string;
  amount: number;
  type?: 'fee';
  subType?: FeeTypeSlug;
  negativeStyle?: boolean;
};

export type PriceDetailsRowsOptions = {
  showAmountBeforeTaxes?: boolean;
  showTaxDetails?: boolean;
  // If passed, will display one line per spare part, instead of the "Services" line
  sparePartPricesForBase?: {
    sparePart: {
      quantity: number;
      quantityToBeCharged: number;
      sparePartType: {
        name: I18nWording;
        unit: I18nWording | null;
      };
    };
    price: PriceAmountWithDetails | undefined;
  }[];
  exchangeCatalogBreakdown?: {
    replacementAmountBeforeTaxes: number;
    currentAmountBeforeTaxes: number;
  };
};

export function usePriceDetailsRows(
  price: PriceAmountWithDetails | PriceAmount | undefined | null,
  options: PriceDetailsRowsOptions
): Row[][] {
  const { _db } = useDatabaseTranslation();

  if (!price) {
    return [];
  }

  const noVATApplied = price.noVATApplied;
  const showTaxDetails = noVATApplied ? false : options.showTaxDetails;
  const showAmountBeforeTaxes = noVATApplied
    ? true
    : showTaxDetails
      ? false
      : options.showAmountBeforeTaxes;

  const sections: Row[][] = [];

  if ('details' in price) {
    const base = price.details.find((detail) => detail.type === 'base');
    const fees = price.details.filter((detail) => detail.type === 'fee');
    const hasFees = fees.some((fee) => fee.amount > 0 || fee.subType === 'client-handling');

    // Add the services section
    if (base) {
      const rows: Row[] = [];

      // 1. Add the services row or spare parts rows
      if (options.sparePartPricesForBase) {
        rows.push(
          ...options.sparePartPricesForBase
            .filter(({ sparePart }) => !!sparePart.quantityToBeCharged)
            .map(({ sparePart, price }) => {
              const sparePartBase = price?.details.find((detail) => detail.type === 'base');

              if (!sparePartBase) {
                return null;
              }

              return {
                label: `${_db(sparePart.sparePartType.name)} ${displayQuantityToBeCharged(sparePart, _db)}`,
                amount:
                  showAmountBeforeTaxes || showTaxDetails
                    ? sparePartBase.amountBeforeTaxes * sparePart.quantityToBeCharged
                    : sparePartBase.amount * sparePart.quantityToBeCharged,
              };
            })
            .filter((rowOrNull) => !!rowOrNull),
          // Add the spare parts not charged
          ...options.sparePartPricesForBase
            .filter(({ sparePart }) => sparePart.quantity > sparePart.quantityToBeCharged)
            .map(({ sparePart }) => ({
              label: `${_db(sparePart.sparePartType.name)} ${displayQuantityNotCharged(sparePart, _db)}`,
              amount: 0,
            }))
        );
      } else if (options.exchangeCatalogBreakdown) {
        rows.push({
          label: msg({
            id: 'price-with-details.rows.replacement-item-excl-vat',
            message: 'Replacement item (Excl. VAT)',
          }),
          amount: options.exchangeCatalogBreakdown.replacementAmountBeforeTaxes,
        });
        rows.push({
          label: msg({
            id: 'price-with-details.rows.current-item-excl-vat',
            message: 'Current item (Excl. VAT)',
          }),
          amount: -options.exchangeCatalogBreakdown.currentAmountBeforeTaxes,
          negativeStyle: true,
        });
      } else {
        rows.push({
          label: msg({ id: 'price-with-details.rows.services', message: 'Services' }),
          amount: showAmountBeforeTaxes || showTaxDetails ? base.amountBeforeTaxes : base.amount,
        });
      }

      // 2. Deal with discounts
      const discount = price.details
        .filter((detail) => detail.type === 'discount' && detail.subType !== 'refashion')
        .reduce(
          (acc, detail) => {
            acc.amount += detail.amount;
            acc.amountBeforeTaxes += detail.amountBeforeTaxes;

            detail.taxes.forEach((tax) => {
              if (!acc.taxes.find((t) => t.rate === tax.rate)) {
                acc.taxes.push({
                  rate: tax.rate,
                  amount: 0,
                });
              }

              acc.taxes.find((t) => t.rate === tax.rate)!.amount += tax.amount;
            });

            return acc;
          },
          {
            amount: 0,
            amountBeforeTaxes: 0,
            currency: price.currency,
            taxes: [] as PriceAmountWithDetails['taxes'],
          }
        );
      const hasDiscount = !!discount.amountBeforeTaxes;

      if (hasDiscount) {
        rows.push({
          label: msg({ id: 'price-with-details.rows.discount', message: 'Discount' }),
          amount:
            showAmountBeforeTaxes || showTaxDetails ? discount.amountBeforeTaxes : discount.amount,
          negativeStyle: true,
        });

        if (showTaxDetails) {
          rows.push({
            label: hasFees
              ? showAmountBeforeTaxes || showTaxDetails
                ? msg({
                    id: 'price-with-details.rows.total-services--with-discount',
                    message: 'Total services with discount',
                  })
                : msg({
                    id: 'price-with-details.rows.total-services-with-discount-incl-vat',
                    message: 'Total services with discount (incl. VAT)',
                  })
              : showAmountBeforeTaxes || showTaxDetails
                ? msg({
                    id: 'price-with-details.rows.total-with-discount',
                    message: 'Total with discount',
                  })
                : msg({
                    id: 'price-with-details.rows.total-with-discount-incl-vat',
                    message: 'Total with discount (incl. VAT)',
                  }),
            amount:
              showAmountBeforeTaxes || showTaxDetails
                ? base.amountBeforeTaxes + discount.amountBeforeTaxes
                : base.amount + discount.amount,
          });
        }
      }

      // 3. Add the taxes and total if we want all the details
      if (showTaxDetails) {
        const baseAndDiscountTaxes = [...base.taxes, ...discount.taxes].reduce(
          (acc, tax) => {
            if (!acc.find((t) => t.rate === tax.rate)) {
              acc.push({
                rate: tax.rate,
                amount: 0,
              });
            }

            acc.find((t) => t.rate === tax.rate)!.amount += tax.amount;

            return acc;
          },
          [] as PriceAmountWithDetails['taxes']
        );

        baseAndDiscountTaxes.forEach((tax) => {
          if (tax.amount !== 0) {
            const taxRate = tax.rate;

            rows.push({
              label: msg({
                id: 'price-with-details.rows.vat-rate',
                message: `VAT ${taxRate}%`,
              }),
              amount: tax.amount,
            });
          }
        });

        if (hasDiscount) {
          rows.push({
            label: hasFees
              ? msg({
                  id: 'price-with-details.rows.total-services-with-discount-incl-vat',
                  message: 'Total services with discount (incl. VAT)',
                })
              : msg({
                  id: 'price-with-details.rows.total-with-discount-incl-vat',
                  message: 'Total with discount (incl. VAT)',
                }),
            amount: base.amount + discount.amount,
          });
        } else {
          rows.push({
            label: hasFees
              ? msg({
                  id: 'price-with-details.rows.total-services-incl-vat',
                  message: 'Total services (incl. VAT)',
                })
              : msg({
                  id: 'price-with-details.rows.total-incl-vat',
                  message: 'Total (incl. VAT)',
                }),
            amount: base.amount,
          });
        }
      }

      // 4. Deal with refashion bonus
      const refashion = price.details.find(
        (detail) => detail.type === 'discount' && detail.subType === 'refashion'
      );
      const hasRefashion = !!refashion?.amount;

      if (hasRefashion) {
        rows.push({
          label: msg({ id: 'price-with-details.rows.refashion-bonus', message: 'Repair bonus' }),
          amount: refashion.amount,
        });

        if (showTaxDetails && hasDiscount) {
          rows.push({
            label: hasFees
              ? msg({
                  id: 'price-with-details.rows.total-services-with-discount-and-bonus-incl-vat',
                  message: 'Total services with discount and bonus (incl. VAT)',
                })
              : msg({
                  id: 'price-with-details.rows.total-with-discount-and-bonus-incl-vat',
                  message: 'Total with discount and bonus (incl. VAT)',
                }),
            amount: base.amount + discount.amount + refashion.amount,
          });
        } else if (showTaxDetails) {
          rows.push({
            label: hasFees
              ? msg({
                  id: 'price-with-details.rows.total-services-with-bonus-incl-vat',
                  message: 'Total services with bonus (incl. VAT)',
                })
              : msg({
                  id: 'price-with-details.rows.total-with-bonus-incl-vat',
                  message: 'Total with bonus (incl. VAT)',
                }),
            amount: base.amount + discount.amount + refashion.amount,
          });
        }
      }

      sections.push(rows);
    }

    // Add the fees section
    if (hasFees) {
      const feesGroupedByType = fees.reduce(
        (acc, currentFee) => {
          const fee = acc.get(currentFee.subType) ?? {
            type: currentFee.subType,
            amount: 0,
            amountBeforeTaxes: 0,
            taxes: [] as PriceAmountWithDetails['taxes'],
          };

          fee.amount += currentFee.amount;
          fee.amountBeforeTaxes += currentFee.amountBeforeTaxes;

          currentFee.taxes.forEach((tax) => {
            if (!fee.taxes.find((t) => t.rate === tax.rate)) {
              fee.taxes.push({
                rate: tax.rate,
                amount: 0,
              });
            }

            fee.taxes.find((t) => t.rate === tax.rate)!.amount += tax.amount;
          });

          acc.set(currentFee.subType, fee);

          return acc;
        },
        new Map<
          string,
          {
            type: PriceFeeDetail['subType'];
            amount: number;
            amountBeforeTaxes: number;
            taxes: PriceAmountWithDetails['taxes'];
          }
        >()
      );

      const rows: Row[] = [];

      feesGroupedByType.forEach((fee) => {
        const label = FEE_TYPE_LABELS[fee.type];

        rows.push({
          label,
          amount: showAmountBeforeTaxes || showTaxDetails ? fee.amountBeforeTaxes : fee.amount,
          type: 'fee',
          subType: fee.type,
        });

        if (showTaxDetails) {
          fee.taxes.forEach((tax) => {
            if (tax.amount !== 0) {
              const taxRate = tax.rate;

              rows.push({
                label: msg({
                  id: 'price-with-details.rows.vat-rate',
                  message: `VAT ${taxRate}%`,
                }),
                amount: tax.amount,
              });
            }
          });
        }
      });

      // Add the total fees row only if there are multiple fees or if we want all the details
      if (fees.length > 1 || showTaxDetails) {
        rows.push({
          label:
            showAmountBeforeTaxes && !showTaxDetails
              ? msg({
                  id: 'price-with-details.rows.total-fees',
                  message: 'Total fees',
                })
              : msg({
                  id: 'price-with-details.rows.total-fees-incl-vat',
                  message: 'Total fees (incl. VAT)',
                }),
          amount: fees.reduce(
            (acc, fee) =>
              acc + (showAmountBeforeTaxes && !showTaxDetails ? fee.amountBeforeTaxes : fee.amount),
            0
          ),
        });
      }

      sections.push(rows);
    }
  }

  // Add the total section
  const rows: Row[] = [
    {
      label:
        showAmountBeforeTaxes || showTaxDetails
          ? msg({
              id: 'price-with-details.rows.total',
              message: 'Total',
            })
          : msg({ id: 'price-with-details.rows.total-incl-vat', message: 'Total (incl. VAT)' }),
      amount: showAmountBeforeTaxes || showTaxDetails ? price.amountBeforeTaxes : price.amount,
    },
  ];

  if (showTaxDetails) {
    price.taxes.forEach((tax) => {
      if (tax.amount !== 0) {
        const taxRate = tax.rate;

        rows.push({
          label: msg({
            id: 'price-with-details.rows.vat-rate',
            message: `VAT ${taxRate}%`,
          }),
          amount: tax.amount,
        });
      }
    });

    rows.push({
      label: msg({ id: 'price-with-details.rows.total-incl-vat', message: 'Total (incl. VAT)' }),
      amount: price.amount,
    });
  }

  sections.push(rows);

  return sections;
}

export const FEE_TYPE_LABELS = {
  'shipping-management': msg({
    id: 'price-with-details.rows.shipping-management',
    message: 'Shipping management fees',
  }),
  cancellation: msg({
    id: 'price-with-details.rows.cancellation',
    message: 'Cancellation fees',
  }),
  'client-shipping-management': msg({
    id: 'price-with-details.rows.client-shipping-management',
    message: 'Shipping fees',
  }),
  'client-handling': msg({
    id: 'price-with-details.rows.client-handling',
    message: 'Handling fee',
  }),
  'paid-estimate-advance': msg({
    id: 'price-with-details.rows.paid-estimate-advance',
    message: 'Paid estimate advance',
  }),
};
